<template>
  <base-screen-tab
    :selected-reference.sync="selectedReference"
    :search-input.sync="searchInputModel"
    :review="review"
    :current-researcher="currentResearcher"
    :pending-connection.sync="pendingConnectionModel"
    :pending-connection-query="pendingConnectionQuery"
    :history-connection="historyConnection"
    :history-connection-query="historyConnectionQuery"
    :filtering-input="filteringInput"
    :disable-select-next-on-vote="disableSelectNextOnVote"
    :hide-on-undo="hideOnUndo"
    :show-full-text="fullTextReview"
    :get-vote-by-current-user-func="getVoteByCurrentUserFunc"
    :revoke-vote-func="revokeVoteFunc"
    :should-hide-reference-func="shouldHideReferenceFunc"
    :should-hide-reference-based-on-vote-func="
      shouldHideReferenceBasedOnVoteFunc
    "
    :can-undo-vote-on-reference-func="canUndoVoteOnReference"
    @clear-filtering-input="$emit('clear-filtering-input')"
  >
    <template v-if="!fullTextReview" #filters>
      <v-checkbox
        v-model="showAbstracts"
        class="mt-0"
        label="Show Abstracts"
        hide-details
      />
    </template>
    <template #screen-card="{ reference, ...cardProps }">
      <screen-card
        v-if="cardProps.shouldShowReference"
        :key="reference.id"
        :reference="reference"
        :review="review"
        :current-researcher="currentResearcher"
        :hide-abstracts="!showAbstracts"
        :hide="cardProps.hide"
        :animate-open="cardProps.animateOpen"
        :search-highlights="cardProps.searchHighlights"
        :voting-only="restrictToVotingOnly"
        :full-text-review="fullTextReview"
        @open="cardProps.onOpen"
        @edit-field="cardProps.onEditField"
        @saving-vote="cardProps.onSavingVote"
        @saved-vote="cardProps.onSavedVote"
        @saving-outcome="cardProps.onSavingOutcome"
        @saved-outcome="cardProps.onSavedOutcome"
        @revoke-outcome="cardProps.onRevokeOutcome"
        @hidden="cardProps.onHidden"
        @animated-open="cardProps.onAnimatedOpen"
      />
    </template>
    <template #focus-actions="eventHandlers">
      <screen-tab-focus-view-actions
        v-if="selectedReference"
        :review="review"
        :reference="selectedReference"
        :current-researcher="currentResearcher"
        :voting-only="restrictToVotingOnly"
        :full-text-review="fullTextReview"
        @saving-vote="eventHandlers.onSavingVote"
        @saved-vote="eventHandlers.onSavedVote"
        @saving-outcome="eventHandlers.onSavingOutcome"
        @saved-outcome="eventHandlers.onSavedOutcome"
        @marked="eventHandlers.onMarkedDuplicate"
      />
    </template>
    <template #empty-state>
      <slot name="empty-state"></slot>
    </template>
  </base-screen-tab>
</template>

<script>
import BaseScreenTab from './BaseScreenTab';
import ScreenCard from './ScreenCard';
import ScreenTabFocusViewActions from './ScreenTabFocusViewActions';
import {
  Review,
  Researcher,
  ReferenceOffsetConnection,
  ReferenceFilteringInputDto,
} from '@/models';

export default {
  name: 'ScreeningWorkflowPanel',
  components: {
    BaseScreenTab,
    ScreenCard,
    ScreenTabFocusViewActions,
  },
  props: {
    // Review & current user props
    review: {
      type: Review,
      required: true,
    },
    currentResearcher: {
      type: Researcher,
      required: true,
    },
    // GraphQL Connections
    pendingConnection: {
      type: ReferenceOffsetConnection,
      default: null,
    },
    historyConnection: {
      type: ReferenceOffsetConnection,
      default: null,
    },
    pendingConnectionQuery: {
      type: Object,
      required: true,
    },
    historyConnectionQuery: {
      type: Object,
      default: null,
    },
    // Filtering models
    filteringInput: {
      type: ReferenceFilteringInputDto,
      required: true,
    },
    searchInput: {
      type: String,
      default: null,
    },
    // Misc props
    restrictToVotingOnly: {
      type: Boolean,
      default: false,
    },
    disableSelectNextOnVote: {
      type: Boolean,
      default: false,
    },
    hideOnUndo: {
      type: Boolean,
      default: false,
    },
    fullTextReview: {
      type: Boolean,
      default: false,
    },
    // Functions
    getVoteByCurrentUserFunc: {
      type: Function,
      default: () => undefined,
    },
    revokeVoteFunc: {
      type: Function,
      default: () => undefined,
    },
    shouldHideReferenceFunc: {
      type: Function,
      default: () => true,
    },
    shouldHideReferenceBasedOnVoteFunc: {
      type: Function,
      default: () => true,
    },
  },
  emits: [
    'update:searchInput',
    'update:pendingConnection',
    'clear-filtering-input',
    'selected-reference-change',
  ],
  data: () => ({
    selectedReference: null,
    showAbstracts: true,
  }),
  computed: {
    pendingConnectionModel: {
      get: function () {
        return this.pendingConnection;
      },
      set: function (value) {
        this.$emit('update:pendingConnection', value);
      },
    },
    searchInputModel: {
      get: function () {
        return this.searchInput;
      },
      set: function (value) {
        this.$emit('update:searchInput', value);
      },
    },
  },
  watch: {
    selectedReference(selectedReference) {
      this.$emit('selected-reference-change', selectedReference);
    },
  },
  methods: {
    canUndoVoteOnReference(reference) {
      return this.fullTextReview
        ? reference?.isPendingExtraction()
        : reference?.isPendingFullTextReview();
    },
  },
};
</script>
